import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, Flame, Trophy, Users, ExternalLink, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'all', label: 'All', emoji: '📰' },
  { key: 'pro', label: 'Pro Tour', emoji: '🏆' },
];

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diffMs = Date.now() - t;
    const diffH = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
}

function NewsCard({ item, onClick }) {
  const isExternal = !!item.external;
  const tagColor = {
    pro: 'bg-blue-500/10 text-blue-700 border-blue-200',
    dmv: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
    bundle: 'bg-bundle-gold/15 text-amber-700 border-amber-300',
  }[item.category] || 'bg-gray-100 text-gray-600 border-gray-200';

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`news-card-${item.news_id}`}
      className="flex-shrink-0 w-[260px] snap-start bg-white border border-gray-200 rounded-xl overflow-hidden text-left hover:shadow-lg hover:border-bundle-green/40 transition-all group"
    >
      {/* Thumbnail */}
      <div className="relative w-full h-32 bg-gradient-to-br from-bundle-green via-emerald-700 to-bundle-green-dark overflow-hidden">
        {item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Newspaper className="w-10 h-10 text-white/40" />
          </div>
        )}
        <div className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${tagColor}`}>
          {item.source_tag || item.source}
        </div>
        {isExternal && (
          <div className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-full">
            <ExternalLink className="w-3 h-3" />
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-3">
        <p className="text-sm font-bold text-gray-900 leading-tight line-clamp-2 group-hover:text-bundle-green transition-colors">
          {item.title}
        </p>
        {item.summary && (
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.summary}</p>
        )}
        <div className="flex items-center justify-between mt-2 text-[10px] text-gray-400">
          <span>{item.source}</span>
          <span>{timeAgo(item.fetched_at || item.published_at)}</span>
        </div>
      </div>
    </button>
  );
}

function FeaturedRail({ featured, navigate }) {
  if (!featured) return null;
  const { shot_of_day, trending_golfers } = featured;
  const hasShot = !!shot_of_day;
  const hasTrending = Array.isArray(trending_golfers) && trending_golfers.length > 0;
  if (!hasShot && !hasTrending) return null;

  return (
    <div className="flex gap-3 overflow-x-auto px-4 pb-2 mt-1 scrollbar-hide snap-x">
      {/* Shot of the Day */}
      {hasShot && (
        <button
          type="button"
          onClick={() => navigate(`/social?post=${shot_of_day.post_id}`)}
          data-testid="featured-shot-of-day"
          className="flex-shrink-0 w-[260px] snap-start bg-gradient-to-br from-amber-50 to-orange-100 border border-amber-300 rounded-xl overflow-hidden text-left hover:shadow-lg transition-all"
        >
          <div className="relative w-full h-28 bg-gray-200 overflow-hidden">
            {shot_of_day.media_url ? (
              <img src={shot_of_day.media_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-200 to-orange-300">
                <Flame className="w-10 h-10 text-orange-700" />
              </div>
            )}
            <div className="absolute top-2 left-2 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1">
              <Flame className="w-3 h-3" /> Shot of the Day
            </div>
          </div>
          <div className="p-3">
            <p className="text-sm font-bold text-gray-900">{shot_of_day.user_name}</p>
            {shot_of_day.caption && <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">{shot_of_day.caption}</p>}
            <div className="flex items-center gap-1 mt-1.5 text-xs text-orange-700 font-bold">
              <Heart className="w-3 h-3 fill-current" />
              <span>{shot_of_day.likes} {shot_of_day.likes === 1 ? 'like' : 'likes'}</span>
            </div>
          </div>
        </button>
      )}
      {/* Trending Golfers */}
      {hasTrending && (
        <button
          type="button"
          onClick={() => navigate('/leaderboard')}
          data-testid="featured-trending-golfers"
          className="flex-shrink-0 w-[260px] snap-start bg-gradient-to-br from-emerald-50 to-green-100 border border-emerald-300 rounded-xl overflow-hidden text-left hover:shadow-lg transition-all"
        >
          <div className="bg-bundle-green text-white px-3 py-2 flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-wider">Trending Golfers</span>
            <span className="ml-auto text-[10px] opacity-80">7d</span>
          </div>
          <div className="p-3 space-y-1.5">
            {trending_golfers.slice(0, 5).map((g, i) => (
              <div key={g.user_id} className="flex items-center gap-2">
                <span className={`w-5 text-center text-xs font-bold ${i === 0 ? 'text-bundle-gold' : i === 1 ? 'text-gray-500' : i === 2 ? 'text-amber-700' : 'text-gray-400'}`}>{i + 1}</span>
                {g.user_picture ? (
                  <img src={g.user_picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-bundle-green text-white text-[10px] font-bold flex items-center justify-center">
                    {(g.user_name || '?').slice(0, 1).toUpperCase()}
                  </div>
                )}
                <span className="text-xs text-gray-800 flex-1 truncate">{g.user_name}</span>
                <span className="text-[10px] text-bundle-green font-bold">{g.round_count} rds</span>
              </div>
            ))}
          </div>
        </button>
      )}
    </div>
  );
}

export default function NewsHub() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const fetchNews = useCallback(async (category) => {
    setLoading(true);
    try {
      const url = `${API}/news/feed?limit=20${category && category !== 'all' ? `&category=${category}` : ''}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (res.ok) {
        const d = await res.json();
        setItems(d.items || []);
      }
    } catch (e) {
      console.error('news fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeatured = useCallback(async () => {
    try {
      const res = await fetch(`${API}/news/featured`, { cache: 'no-store' });
      if (res.ok) {
        const d = await res.json();
        setFeatured(d);
      }
    } catch (e) {
      console.error('featured fetch failed', e);
    }
  }, []);

  useEffect(() => { fetchNews(tab); }, [tab, fetchNews]);
  useEffect(() => { fetchFeatured(); }, [fetchFeatured]);

  const handleClick = (item) => {
    if (item.external && item.link) {
      window.open(item.link, '_blank', 'noopener,noreferrer');
    } else if (item.link) {
      navigate(item.link);
    }
  };

  // Always render the section — show skeletons while loading, empty state if truly nothing.
  // (Prior version returned null on empty, which hid the hub silently if a fetch failed.)

  return (
    <div className="mb-5 -mx-4" data-testid="news-hub">
      {/* Header + tabs */}
      <div className="px-4 mb-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Newspaper className="w-4 h-4 text-bundle-green" />
            <h2 className="font-heading font-bold text-base text-bundle-green">Bundle News</h2>
          </div>
        </div>
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`news-tab-${t.key}`}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${
                tab === t.key
                  ? 'bg-bundle-green text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-bundle-green/40'
              }`}
            >
              <span className="mr-1">{t.emoji}</span>{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Featured rail (Shot of Day + Trending Golfers) — only shown on All tab */}
      {tab === 'all' && featured && (
        <FeaturedRail featured={featured} navigate={navigate} />
      )}

      {/* News carousel */}
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide snap-x">
        {loading && items.length === 0 ? (
          // Skeleton cards
          [1, 2, 3].map(i => (
            <div key={i} className="flex-shrink-0 w-[260px] bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="w-full h-32 bg-gray-100 animate-pulse" />
              <div className="p-3 space-y-2">
                <div className="h-3 bg-gray-100 rounded animate-pulse" />
                <div className="h-3 bg-gray-100 rounded w-2/3 animate-pulse" />
              </div>
            </div>
          ))
        ) : items.length === 0 ? (
          <div className="w-full px-4 py-6 text-center text-sm text-gray-500">
            No {tab !== 'all' ? TABS.find(t => t.key === tab)?.label.toLowerCase() : ''} headlines yet — check back soon.
          </div>
        ) : (
          items.map(item => (
            <NewsCard key={item.news_id} item={item} onClick={() => handleClick(item)} />
          ))
        )}
      </div>
    </div>
  );
}
